import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Receipt, RefreshCw, Loader2, AlertTriangle, Check,
  Building2, ListFilter, ChevronDown,
} from 'lucide-react';
import { apiService } from '../services/api.service';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

/**
 * Pilotage de la facturation, écran LogFit (jamais organisateur).
 *
 * Vue de contrôle, sans action : la clôture du 1er du mois émet le relevé, la
 * note de crédit des ventes et la facture de commission, puis les envoie
 * (`scripts/issue_monthly_statements.py`). Elle se relance sans risque sur un
 * mois : c'est le seul geste de rattrapage. Les boutons qui doublaient la
 * clôture ont été retirés le 2026-10-06 (ils dataient du rattrapage février →
 * août et laissaient croire que le chemin normal était manuel).
 */

interface MonthRow {
  organization_id: string;
  organization_name: string;
  vat_declared: boolean;
  year: number;
  month: number;
  sales_count: number;
  total_gross_cents: number;
  commission_cents: number;
  period_closed: boolean;
  statement_number: string | null;
  issued_at: string | null;
  credit_note_number: string | null;
  commission_invoice_id: number | null;
  commission_invoice_sent: boolean;
}

/** Où en est un mois : la prochaine étape à faire, dans l'ordre du cycle. */
type Status = 'in_progress' | 'to_issue' | 'invoice_to_create' | 'to_send' | 'sent';
const STATUSES: Status[] = ['in_progress', 'to_issue', 'invoice_to_create', 'to_send', 'sent'];

const statusOf = (row: MonthRow): Status => {
  if (!row.period_closed) return 'in_progress';
  if (!row.statement_number) return 'to_issue';
  if (!row.commission_invoice_id) return 'invoice_to_create';
  if (!row.commission_invoice_sent) return 'to_send';
  return 'sent';
};

const euros = (cents: number) =>
  new Intl.NumberFormat('fr-BE', { style: 'currency', currency: 'EUR' }).format(cents / 100);

const AdminBilling: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState<MonthRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [orgFilter, setOrgFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<Status | 'all'>('all');

  const organizations = useMemo(() => {
    const byId = new Map<string, string>();
    (rows || []).forEach((r) => byId.set(r.organization_id, r.organization_name));
    return Array.from(byId, ([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }, [rows]);

  const visibleRows = useMemo(() => (rows || []).filter((r) =>
    (orgFilter === 'all' || r.organization_id === orgFilter) &&
    (statusFilter === 'all' || statusOf(r) === statusFilter)
  ), [rows, orgFilter, statusFilter]);

  // Le décompte d'un état suit le filtre d'organisation : il dit ce qu'on
  // trouvera en le choisissant, pas un total qui ne s'afficherait jamais.
  const statusCount = (status: Status) => (rows || []).filter((r) =>
    (orgFilter === 'all' || r.organization_id === orgFilter) && statusOf(r) === status
  ).length;

  const monthLabel = (year: number, month: number) =>
    new Date(year, month - 1, 1).toLocaleDateString(i18n.language, {
      month: 'long',
      year: 'numeric',
    });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.get('/admin/statements');
      setRows(res.data.months as MonthRow[]);
    } catch (err: any) {
      setError(err?.response?.data?.detail || t('adminBilling.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Receipt className="h-5 w-5 text-muted-foreground" />
          <div>
            <h1 className="text-xl font-semibold md:text-2xl">{t('adminBilling.title')}</h1>
            <p className="text-xs text-muted-foreground md:text-sm">
              {t('adminBilling.subtitle')}
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          {t('common.refresh')}
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading && !rows && (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {rows && rows.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center text-sm text-muted-foreground">
            {t('adminBilling.empty')}
          </CardContent>
        </Card>
      )}

      {rows && rows.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline">
            {t('adminBilling.filters.count', { shown: visibleRows.length, total: rows.length })}
          </Badge>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Badge variant="outline"
                     className="flex cursor-pointer items-center gap-1 transition-colors hover:bg-white/[0.06]">
                <Building2 size={14} />
                {orgFilter === 'all'
                  ? t('adminBilling.filters.allOrganizations')
                  : organizations.find((o) => o.id === orgFilter)?.name}
                <ChevronDown size={12} />
              </Badge>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="max-h-80 overflow-y-auto">
              <DropdownMenuItem onClick={() => setOrgFilter('all')}>
                {t('adminBilling.filters.allOrganizations')}
              </DropdownMenuItem>
              {organizations.map((o) => (
                <DropdownMenuItem key={o.id} onClick={() => setOrgFilter(o.id)}>
                  {o.name} ({rows.filter((r) => r.organization_id === o.id).length})
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Badge variant="outline"
                     className="flex cursor-pointer items-center gap-1 transition-colors hover:bg-white/[0.06]">
                <ListFilter size={14} />
                {statusFilter === 'all'
                  ? t('adminBilling.filters.allStatuses')
                  : t(`adminBilling.filters.status.${statusFilter}`)}
                <ChevronDown size={12} />
              </Badge>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem onClick={() => setStatusFilter('all')}>
                {t('adminBilling.filters.allStatuses')}
              </DropdownMenuItem>
              {STATUSES.map((s) => (
                <DropdownMenuItem key={s} onClick={() => setStatusFilter(s)}>
                  {t(`adminBilling.filters.status.${s}`)} ({statusCount(s)})
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {rows && rows.length > 0 && visibleRows.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center text-sm text-muted-foreground">
            {t('adminBilling.filters.noMatch')}
          </CardContent>
        </Card>
      )}

      {visibleRows.length > 0 && (
        <div className="rounded-lg border bg-card">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-3 font-medium">{t('adminBilling.columns.organization')}</th>
                  <th className="px-4 py-3 font-medium">{t('adminBilling.columns.period')}</th>
                  <th className="px-4 py-3 text-right font-medium">{t('adminBilling.columns.sales')}</th>
                  <th className="px-4 py-3 text-right font-medium">{t('adminBilling.columns.gross')}</th>
                  <th className="px-4 py-3 text-right font-medium">{t('adminBilling.columns.commission')}</th>
                  <th className="px-4 py-3 font-medium">{t('adminBilling.columns.statement')}</th>
                  <th className="px-4 py-3 text-right font-medium">{t('adminBilling.columns.invoice')}</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row) => (
                  <tr key={`${row.organization_id}-${row.year}-${row.month}`} className="border-b last:border-0">
                    <td className="px-4 py-3">
                      <div className="font-medium">{row.organization_name}</div>
                      {!row.vat_declared && (
                        <span className="text-xs text-warning">
                          {t('adminBilling.noVatRegime')}
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 capitalize">
                      {monthLabel(row.year, row.month)}
                      {!row.period_closed && (
                        <span className="ml-2 text-xs text-muted-foreground">
                          {t('adminBilling.inProgress')}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{row.sales_count}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{euros(row.total_gross_cents)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{euros(row.commission_cents)}</td>
                    <td className="px-4 py-3">
                      {row.statement_number ? (
                        <div className="space-y-1">
                          <Badge variant="secondary">{row.statement_number}</Badge>
                          {row.credit_note_number && (
                            <div>
                              <Badge variant="outline" className="text-xs">
                                {row.credit_note_number}
                              </Badge>
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      {row.commission_invoice_sent ? (
                        <Badge variant="outline" className="text-xs">
                          <Check className="mr-1 h-3 w-3" />
                          {t('adminBilling.invoiceSent')}
                        </Badge>
                      ) : row.commission_invoice_id ? (
                        <span className="text-xs text-warning">{t('adminBilling.invoiceDraft')}</span>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminBilling;
